import { ChevronRight, Mail, MessageSquare, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface TemplateRow {
  id: string;
  templateKey: string;
  channel: 'email' | 'sms' | 'whatsapp';
  locale: string;
  subject: string | null;
  bodyPreview: string;
  version: number;
  isOverride: boolean;
}

interface TemplatesResponse {
  templates: TemplateRow[];
}

function channelIcon(channel: TemplateRow['channel']) {
  if (channel === 'email') return <Mail className="size-4" strokeWidth={1.75} />;
  if (channel === 'sms') return <MessageSquare className="size-4" strokeWidth={1.75} />;
  return <Phone className="size-4" strokeWidth={1.75} />;
}

export function TemplatesPage() {
  const [data, setData] = useState<TemplatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<'all' | TemplateRow['channel']>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch('/api/notification-templates', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          setError(`Failed to load (HTTP ${res.status})`);
          return;
        }
        const body = (await res.json()) as TemplatesResponse;
        setData(body);
      })
      .catch(() => setError('Network error'));
  }, []);

  const filtered = (data?.templates ?? []).filter((t) => {
    if (channelFilter !== 'all' && t.channel !== channelFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return t.templateKey.toLowerCase().includes(q) || (t.subject ?? '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-6 px-4 py-6 md:px-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Templates</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Customise the subject + body of every notification your customers receive. Defaults ship
          from the platform; overrides save against your tenant only.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'email', 'sms', 'whatsapp'] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setChannelFilter(c)}
            className={`rounded-full px-3 py-1 text-xs font-medium press ${
              channelFilter === c
                ? 'bg-primary text-primary-foreground'
                : 'border border-border bg-surface text-muted-foreground hover:bg-secondary'
            }`}
          >
            {c === 'all' ? 'All channels' : c}
          </button>
        ))}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search trigger key…"
          className="h-8 w-56 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Search templates"
        />
      </div>

      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {!error && data === null && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">No templates match the filter.</p>
      )}

      {data && filtered.length > 0 && (
        <ul className="divide-y divide-border rounded-2xl bg-surface ring-1 ring-border elevate overflow-hidden">
          {filtered.map((t) => (
            <li key={t.id}>
              <Link
                to={`/communications/templates/${t.templateKey}/${t.channel}/${t.locale}`}
                className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-secondary/30 press"
              >
                <div className="flex items-center gap-3">
                  <span className="grid size-8 place-items-center rounded-lg bg-primary/10 text-primary">
                    {channelIcon(t.channel)}
                  </span>
                  <div>
                    <div className="font-mono text-xs text-foreground">{t.templateKey}</div>
                    <div className="text-sm">
                      {t.subject ?? (
                        <span className="italic text-muted-foreground">No subject</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {t.isOverride ? (
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Customised · v<span className="num">{t.version}</span>
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Default</span>
                  )}
                  <ChevronRight className="size-4 text-muted-foreground" strokeWidth={1.75} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
